import { computed } from 'vue';

import { useLocalStorage } from '@/composables/useLocalStorage';
import { isAdvancePlan, isEnterprisePlan } from '@/models/auth/customer-plan.enum';
import { useIdentityStore } from '@/stores/identity';
import { useTrackingStore } from '@/stores/tracking';

const MAJOR_VERSION_NUMBER = 8;
const MAJOR_VERSION_START_DATE = new Date('2025-06-16'); // Controls Major Version Number

const HOURS_IN_MS = 1000 * 60 * 60;
const MIN_DAYS_BETWEEN_NOTIFICATIONS = 5;
const NOTIFICATIONS_PER_MONTH = 2;

export function useVisionModelUpgrade() {
  const identityStore = useIdentityStore();
  const trackingStore = useTrackingStore();
  const lastVisionModelUpgradeDetected = useLocalStorage('visionModelUpgradeDetected', null);
  const upgradeMessageShownForVersion = useLocalStorage('visionUpgradeMessageShown', null);

  const isEnterpriseCustomer = computed(() => {
    const planType = identityStore.currentPlanType;
    return planType ? isEnterprisePlan(planType) : false;
  });

  const isAdvanceCustomer = computed(() => {
    const planType = identityStore.currentPlanType;
    return planType ? isAdvancePlan(planType) : false;
  });

  function getToday() {
    return new Date();
  }

  function generateVersionNumber() {
    const now = getToday();
    const daysSinceStart = Math.floor((now - MAJOR_VERSION_START_DATE) / (HOURS_IN_MS * 24));

    const AVERAGE_DAYS_PER_MONTH = 30.44;
    const daysPerPeriod = AVERAGE_DAYS_PER_MONTH / NOTIFICATIONS_PER_MONTH;
    const periodsSinceStart = Math.floor(daysSinceStart / daysPerPeriod);
    const minorVersionNumber = periodsSinceStart;

    return {
      major: MAJOR_VERSION_NUMBER,
      minor: minorVersionNumber,
      full: `${MAJOR_VERSION_NUMBER}.${minorVersionNumber}`,
    };
  }

  function trackVisionModelUpdated(action) {
    trackingStore.track('Vision Model Updated', {
      action,
      visionModelUpdateVersion: generateVersionNumber().full,
    });
  }

  function getMonthPeriod(date) {
    const day = date.getDate();
    const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    const daysPerPeriod = Math.ceil(daysInMonth / NOTIFICATIONS_PER_MONTH);
    return Math.floor((day - 1) / daysPerPeriod);
  }

  function shouldShowUpgradeNotification() {
    const now = getToday();
    const currentPeriod = getMonthPeriod(now);
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    if (!lastVisionModelUpgradeDetected.value) {
      return true;
    }

    const lastUpgradeDetectedDate = new Date(lastVisionModelUpgradeDetected.value);
    const lastPeriod = getMonthPeriod(lastUpgradeDetectedDate);
    const lastYear = lastUpgradeDetectedDate.getFullYear();
    const lastMonth = lastUpgradeDetectedDate.getMonth();

    const isDifferentPeriod =
      currentYear !== lastYear || currentMonth !== lastMonth || currentPeriod !== lastPeriod;

    if (!isDifferentPeriod) {
      return false;
    }

    const daysSinceLastUpgrade = Math.floor((now - lastUpgradeDetectedDate) / (HOURS_IN_MS * 24));

    return daysSinceLastUpgrade >= MIN_DAYS_BETWEEN_NOTIFICATIONS;
  }

  function getRandomInt(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  function getRandomStatNumber() {
    if (isEnterpriseCustomer.value) {
      return getRandomInt(50000, 100000);
    }

    if (isAdvanceCustomer.value) {
      return getRandomInt(10000, 49999);
    }

    return getRandomInt(2000, 9999);
  }

  function getUpgradeMessageData(majorVersionNumber, minorVersionNumber) {
    const messageIndex = (minorVersionNumber ?? 0) % 4;
    const title = `Vision model ${majorVersionNumber}.${minorVersionNumber} updated 🚀`;
    const brandSignals = getRandomStatNumber();
    const modelIntegrations = getRandomStatNumber();
    const brandSpecificDataPoints = getRandomStatNumber();

    return {
      title,
      messageIndex,
      brandSignals,
      modelIntegrations,
      brandSpecificDataPoints,
    };
  }

  function markUpgradeDetected() {
    lastVisionModelUpgradeDetected.value = new Date().toISOString();
    trackVisionModelUpdated('triggered');
  }

  function shouldShowUpgradeMessage() {
    if (!lastVisionModelUpgradeDetected.value) {
      return false;
    }

    const versionInfo = generateVersionNumber();
    const currentVersion = versionInfo.full;

    return upgradeMessageShownForVersion.value !== currentVersion;
  }

  function markUpgradeMessageAsShown() {
    const versionInfo = generateVersionNumber();
    upgradeMessageShownForVersion.value = versionInfo.full;
    trackVisionModelUpdated('message_shown');
  }

  const currentStatusMessage = computed(() => {
    const versionInfo = generateVersionNumber();
    return `Vision model ${versionInfo.full} updated`;
  });

  return {
    shouldShowUpgradeNotification,
    currentStatusMessage,
    markUpgradeDetected,
    getUpgradeMessageData,
    generateVersionNumber,
    shouldShowUpgradeMessage,
    markUpgradeMessageAsShown,
    trackVisionModelUpdated,
  };
}
